// Bilingual UI strings for HorseAppCompare.
export const TRANSLATIONS = {
  en: {
    brand: "HorseAppCompare",
    slogan: "Find the perfect horse app for your situation.",
    nav: {
      directory: "Directory",
      rankings: "Top 100",
      compare: "Compare",
      categories: "Categories",
      advisor: "Advisor",
    },
    hero: {
      eyebrow: "The Global Equine Software Intelligence Platform",
      title: "Compare every horse app in the world.",
      lede:
        "Objective profiles, side-by-side comparisons, and AI-powered recommendations for owners, riders, trainers, breeders, barns and equine professionals.",
      ctaBrowse: "Browse the directory",
      ctaAdvisor: "Ask the advisor",
      stat1Label: "Apps catalogued",
      stat2Label: "Functionalities tracked",
      stat3Label: "Regions covered",
    },
    onboarding: {
      badge: "Now live worldwide",
      lead: "Now onboarding global apps —",
      highlight: "claim your free position",
      cta: "Register app",
    },
    sections: {
      featured: "Featured this month",
      featuredSub: "Hand-picked apps with the broadest functionality and strongest community ratings.",
      categories: "Browse by what you do",
      categoriesSub: "Every app is mapped against 50+ functionalities across 9 categories.",
      ranking: "Top 100 worldwide",
      rankingSub: "Composite score from features, ratings, AI capability and adoption.",
      newlyListed: "Just listed this week",
      newlyListedHeadline: "Fresh apps to discover",
      newlyListedSub: "Fresh apps that recently joined the platform — be among the first to discover them.",
      advisor: "Talk to the advisor",
      advisorSub:
        "Describe your situation — boarding 12 horses, dressage rider, breeding farm — and our advisor narrows the catalogue for you.",
    },
    categories: {
      training: "Training",
      health: "Health",
      nutrition: "Nutrition",
      competitions: "Competitions",
      breeding: "Breeding",
      stable: "Stable Management",
      professional: "Professional",
      safety: "Safety",
      ai: "AI Features",
    },
    features: {
      training_log: "Training log",
      gps_tracking: "GPS tracking",
      speed_analysis: "Speed analysis",
      jumping_analysis: "Jumping analysis",
      dressage_analysis: "Dressage analysis",
      training_schedules: "Training schedules",
      coach_support: "Coach support",
      temperature: "Temperature",
      weight_tracking: "Weight tracking",
      injury_management: "Injury management",
      medication: "Medication",
      vaccinations: "Vaccinations",
      deworming: "Deworming",
      vet_records: "Vet records",
      feeding_schedule: "Feeding schedule",
      supplements: "Supplements",
      water_intake: "Water intake",
      hay_analysis: "Hay analysis",
      nutrition_advice: "Nutrition advice",
      competition_planning: "Competition planning",
      results: "Results",
      protocol_analysis: "Protocol analysis",
      start_lists: "Start lists",
      knhs_integration: "KNHS integration",
      fei_integration: "FEI integration",
      pedigree: "Pedigree",
      mare_management: "Mare management",
      pregnancy_planning: "Pregnancy planning",
      foal_registration: "Foal registration",
      ai_breeding_advice: "AI breeding advice",
      genetic_analysis: "Genetic analysis",
      stallion_comparison: "Stallion comparison",
      box_management: "Box management",
      pasture_management: "Pasture management",
      planning: "Planning",
      staff: "Staff",
      task_lists: "Task lists",
      billing: "Billing",
      boarding_clients: "Boarding clients",
      farrier: "Farrier",
      dentist: "Dentist",
      osteopath: "Osteopath",
      physio: "Physiotherapist",
      trainer: "Trainer",
      vet: "Vet",
      emergency_button: "Emergency button",
      live_location: "Live location",
      fall_detection: "Fall detection",
      emergency_contacts: "Emergency contacts",
      sos_alerts: "SOS alerts",
      ai_coach: "AI coach",
      ai_competition_analysis: "AI competition analysis",
      ai_nutrition: "AI nutrition advice",
      ai_injury: "AI injury analysis",
      ai_breeding: "AI breeding advice",
      ai_management: "AI management assistant",
    },
    common: {
      free: "Free",
      premium: "Premium",
      newBadge: "NEW",
      justLaunched: "Just launched",
      listedDaysAgo: "Listed {n} days ago",
      listedToday: "Listed today",
      monthly: "/month",
      yearly: "/year",
      visit: "Visit website",
      addToCompare: "Add to compare",
      remove: "Remove",
      compareNow: "Compare now",
      ofApps: "apps",
      selectAtLeastTwo: "Select at least 2 apps to compare.",
      clearAll: "Clear all",
      backToDirectory: "Back to directory",
      overview: "Overview",
      pricing: "Pricing",
      featuresLabel: "Features",
      platforms: "Platforms",
      languages: "Languages",
      founded: "Founded",
      country: "Country",
      audience: "Best for",
      regions: "Regions",
      filters: "Filters",
      sortBy: "Sort by",
      sortScore: "Highest score",
      sortRating: "Highest rating",
      sortName: "A → Z",
      searchPlaceholder: "Search apps, features, descriptions…",
      noResults: "No apps match these filters.",
      rank: "Rank",
      score: "Score",
      ratingLabel: "Rating",
      reviews: "reviews",
      sponsored: "Sponsored",
    },
    audiences: {
      owners: "Owners",
      riders: "Riders",
      trainers: "Trainers",
      breeders: "Breeders",
      barns: "Barns",
      vets: "Vets",
    },
    advisor: {
      title: "Tell us about your situation",
      placeholder: "e.g. I run a boarding barn with 12 horses and need invoicing.",
      send: "Send",
      thinking: "Thinking…",
      newChat: "New chat",
      examples: [
        "I have a breeding farm and need pedigree tracking.",
        "I'm a dressage rider — what app should I use?",
        "I manage 12 boarding horses and need invoicing.",
        "I want a free safety app for hacking out.",
      ],
      recommended: "Recommended apps",
    },
    regions: ["Worldwide", "Europe", "USA", "Australia"],
    sponsor: {
      nav: "Advertise",
      bannerTitle: "Reach 10,000+ stables and 30,000+ horse contacts worldwide.",
      bannerSub:
        "Targeted promotion campaigns, premium app listings and lead generation across the world's largest equine network.",
      cta: "Become a sponsor",
      pageTitle: "Advertise your brand to the world's equine community.",
      pageLede:
        "We hold first-party data on more than 10,000 stables worldwide and 30,000+ direct contacts — owners, riders, breeders, trainers, barn managers and vets. Reach exactly the audience that matters in the country and sector you choose.",
      stats: [
        { value: "10,000+", label: "stables in our database" },
        { value: "30,000+", label: "direct contacts" },
        { value: "9", label: "sectors targetable" },
        { value: "30+", label: "countries" },
      ],
      campaignTitle: "Targeted promotion campaigns",
      campaignLede:
        "We run end-to-end campaigns to promote your brand or product directly to stables, owners and breeders — in the sectors and countries you select. From copy and creative to delivery and reporting, fully managed.",
      campaignPrice: "From €5,900 / month",
      campaignPerks: [
        "Full audience activation across your selected sectors & countries",
        "Email, in-app and partner-newsletter placements",
        "Dedicated campaign manager + creative production",
        "Weekly performance reports with qualified leads",
        "Ideal for new product launches, breeding services and equine SaaS",
      ],
      campaignCta: "Plan a campaign",
      formatsTitle: "Always-on ad formats",
      formatsLede:
        "Stay top-of-mind across the directory with high-visibility ad placements. Monthly rates, no hidden fees.",
      packages: [
        {
          key: "square",
          name: "Square image block",
          price: "€175 / month",
          perks: [
            "1:1 image placement in app listings",
            "Cycled across category pages",
            "Click + impression analytics",
          ],
        },
        {
          key: "featured",
          name: "Featured app slot",
          price: "€299 / month",
          perks: [
            "Featured slot in the directory & rankings",
            "Highlighted listing in your category",
            "Up to 3 screenshots + video demo",
          ],
        },
        {
          key: "banner",
          name: "Banner ad",
          price: "€499 / month",
          perks: [
            "Banner on the homepage",
            "Banner on category pages",
            "Click + impression analytics",
          ],
        },
        {
          key: "video",
          name: "Video banner",
          price: "€650 / month",
          perks: [
            "15–30s autoplay video on homepage",
            "Premium category pages",
            "Detailed engagement metrics",
          ],
        },
      ],
      sectors: [
        "Owners",
        "Riders",
        "Trainers",
        "Breeders",
        "Stables & pension barns",
        "Vets",
        "Farriers",
        "Equine SaaS",
        "Feed & supplements",
      ],
      countries: ["Netherlands", "Belgium", "Germany", "France", "UK", "USA", "Australia", "Spain", "Sweden", "Italy"],
      idealFor: "Ideal for equine SaaS, breeding stables, supplement brands, insurance, training programmes and equestrian retail.",
      form: {
        title: "Plan your campaign",
        company: "Company",
        name: "Your name",
        email: "Email",
        website: "Website",
        packageLabel: "Interested in",
        message: "Tell us about your target audience, country and goals",
        submit: "Send request",
        success: "Thanks — our advertising team will reach out within 1 working day.",
        error: "Something went wrong. Please try again.",
      },
    },
    footer: {
      tagline: "The Global Equine Software Intelligence Platform.",
      rights: "All rights reserved.",
    },
  },
  nl: {
    brand: "HorseAppCompare",
    slogan: "Vind de perfecte paardenapp voor jouw situatie.",
    nav: {
      directory: "Apps",
      rankings: "Top 100",
      compare: "Vergelijken",
      categories: "Categorieën",
      advisor: "Adviseur",
    },
    hero: {
      eyebrow: "De paardensoftware-gids",
      title: "Vergelijk elke paardenapp ter wereld.",
      lede:
        "Objectieve profielen, naast-elkaar-vergelijkingen en AI-aanbevelingen voor eigenaren, ruiters, trainers, fokkers, stallen en professionals.",
      ctaBrowse: "Bekijk de gids",
      ctaAdvisor: "Vraag de adviseur",
      stat1Label: "Apps in de gids",
      stat2Label: "Functionaliteiten",
      stat3Label: "Regio's",
    },
    onboarding: {
      badge: "Wereldwijd live",
      lead: "We onboarden nu apps wereldwijd —",
      highlight: "claim je gratis positie",
      cta: "Registreer app",
    },
    sections: {
      featured: "Uitgelicht deze maand",
      featuredSub: "Met de hand gekozen apps met de breedste functionaliteit en sterkste reviews.",
      categories: "Bladeren op wat jij doet",
      categoriesSub: "Elke app is geclassificeerd op 50+ functionaliteiten in 9 categorieën.",
      ranking: "Top 100 wereldwijd",
      rankingSub: "Score op basis van functionaliteiten, beoordelingen, AI en adoptie.",
      newlyListed: "Deze week nieuw geplaatst",
      newlyListedHeadline: "Verse apps om te ontdekken",
      newlyListedSub: "Verse apps die net op het platform staan — wees als eerste om ze te ontdekken.",
      advisor: "Praat met de adviseur",
      advisorSub:
        "Beschrijf je situatie — 12 pensionpaarden, dressuurruiter, fokkerij — en onze adviseur filtert de gids voor je.",
    },
    categories: {
      training: "Training",
      health: "Gezondheid",
      nutrition: "Voeding",
      competitions: "Wedstrijden",
      breeding: "Fokkerij",
      stable: "Stalmanagement",
      professional: "Professioneel",
      safety: "Veiligheid",
      ai: "AI-functies",
    },
    features: {
      training_log: "Trainingslogboek",
      gps_tracking: "GPS tracking",
      speed_analysis: "Snelheidsanalyse",
      jumping_analysis: "Springanalyse",
      dressage_analysis: "Dressuuranalyse",
      training_schedules: "Trainingsschema's",
      coach_support: "Coach-ondersteuning",
      temperature: "Temperatuur",
      weight_tracking: "Gewichtstracking",
      injury_management: "Blessurebeheer",
      medication: "Medicatie",
      vaccinations: "Vaccinaties",
      deworming: "Ontworming",
      vet_records: "Dierenarts dossier",
      feeding_schedule: "Voerschema",
      supplements: "Supplementen",
      water_intake: "Wateropname",
      hay_analysis: "Hooi-analyse",
      nutrition_advice: "Voedingsadvies",
      competition_planning: "Wedstrijdplanning",
      results: "Uitslagen",
      protocol_analysis: "Protocolanalyse",
      start_lists: "Startlijsten",
      knhs_integration: "KNHS-koppeling",
      fei_integration: "FEI-koppeling",
      pedigree: "Afstammingen",
      mare_management: "Merrie-management",
      pregnancy_planning: "Drachtplanning",
      foal_registration: "Veulenregistratie",
      ai_breeding_advice: "AI-fokadvies",
      genetic_analysis: "Genetische analyse",
      stallion_comparison: "Hengsten vergelijken",
      box_management: "Boxbeheer",
      pasture_management: "Weidebeheer",
      planning: "Planning",
      staff: "Medewerkers",
      task_lists: "Takenlijsten",
      billing: "Facturatie",
      boarding_clients: "Pensionklanten",
      farrier: "Hoefsmid",
      dentist: "Tandarts",
      osteopath: "Osteopaat",
      physio: "Fysiotherapeut",
      trainer: "Trainer",
      vet: "Dierenarts",
      emergency_button: "Noodknop",
      live_location: "Live locatie",
      fall_detection: "Valdetectie",
      emergency_contacts: "Noodcontacten",
      sos_alerts: "SOS-meldingen",
      ai_coach: "AI Coach",
      ai_competition_analysis: "AI Wedstrijdanalyse",
      ai_nutrition: "AI Voedingsadvies",
      ai_injury: "AI Blessureanalyse",
      ai_breeding: "AI Fokadvies",
      ai_management: "AI Management Assistent",
    },
    common: {
      free: "Gratis",
      premium: "Premium",
      newBadge: "NIEUW",
      justLaunched: "Net gelanceerd",
      listedDaysAgo: "{n} dagen geleden geplaatst",
      listedToday: "Vandaag geplaatst",
      monthly: "/maand",
      yearly: "/jaar",
      visit: "Bezoek website",
      addToCompare: "Vergelijken",
      remove: "Verwijderen",
      compareNow: "Vergelijk nu",
      ofApps: "apps",
      selectAtLeastTwo: "Kies minimaal 2 apps om te vergelijken.",
      clearAll: "Wis alles",
      backToDirectory: "Terug naar de gids",
      overview: "Overzicht",
      pricing: "Prijs",
      featuresLabel: "Functionaliteiten",
      platforms: "Platforms",
      languages: "Talen",
      founded: "Opgericht",
      country: "Land",
      audience: "Geschikt voor",
      regions: "Regio's",
      filters: "Filters",
      sortBy: "Sorteer op",
      sortScore: "Hoogste score",
      sortRating: "Hoogste beoordeling",
      sortName: "A → Z",
      searchPlaceholder: "Zoek apps, functies, omschrijvingen…",
      noResults: "Geen apps passen bij deze filters.",
      rank: "Rang",
      score: "Score",
      ratingLabel: "Beoordeling",
      reviews: "reviews",
      sponsored: "Gesponsord",
    },
    audiences: {
      owners: "Eigenaren",
      riders: "Ruiters",
      trainers: "Trainers",
      breeders: "Fokkers",
      barns: "Stallen",
      vets: "Dierenartsen",
    },
    advisor: {
      title: "Vertel ons je situatie",
      placeholder: "bv. Ik heb een pensionstal met 12 paarden en zoek facturatie.",
      send: "Verstuur",
      thinking: "Aan het denken…",
      newChat: "Nieuw gesprek",
      examples: [
        "Ik heb een fokkerij en wil afstammingen bijhouden.",
        "Ik ben dressuurruiter — welke app past bij mij?",
        "Ik beheer 12 pensionpaarden en zoek facturatie.",
        "Ik wil een gratis veiligheids-app voor buitenritten.",
      ],
      recommended: "Aanbevolen apps",
    },
    regions: ["Wereldwijd", "Europa", "Verenigde Staten", "Australië"],
    sponsor: {
      nav: "Adverteren",
      bannerTitle: "Bereik 10.000+ stallen en 30.000+ paardencontacten wereldwijd.",
      bannerSub:
        "Gerichte promotiecampagnes, premium app-vermeldingen en leadgeneratie op het grootste paardennetwerk ter wereld.",
      cta: "Word sponsor",
      pageTitle: "Adverteer jouw merk bij de wereldwijde paardencommunity.",
      pageLede:
        "We beschikken over first-party data van meer dan 10.000 stallen wereldwijd en 30.000+ directe contactpersonen — eigenaren, ruiters, fokkers, trainers, stalmanagers en dierenartsen. Bereik precies de doelgroep die telt, in het land en de branche die jij kiest.",
      stats: [
        { value: "10.000+", label: "stallen in onze database" },
        { value: "30.000+", label: "directe contactpersonen" },
        { value: "9", label: "branches targetbaar" },
        { value: "30+", label: "landen" },
      ],
      campaignTitle: "Gerichte promotiecampagnes",
      campaignLede:
        "Wij voeren end-to-end campagnes om jouw merk of product direct te promoten bij stallen, eigenaren en fokkers — in de branches en landen die jij kiest. Van copy en creatie tot uitvoering en rapportage, volledig managed. Ook voor fokkerijen dé ideale plaats om te adverteren.",
      campaignPrice: "Vanaf €5.900 / maand",
      campaignPerks: [
        "Volledige doelgroep-activatie in jouw gekozen branches & landen",
        "E-mail, in-app en partner-nieuwsbrief plaatsingen",
        "Toegewezen campagne-manager + creatie-productie",
        "Wekelijkse performance-rapporten met gekwalificeerde leads",
        "Ideaal voor productlanceringen, fokkerij-diensten en equine SaaS",
      ],
      campaignCta: "Plan een campagne",
      formatsTitle: "Always-on advertentieformats",
      formatsLede:
        "Blijf top-of-mind in de gids met goed zichtbare advertentieplaatsen. Maandelijkse tarieven, geen verborgen kosten.",
      packages: [
        {
          key: "square",
          name: "Vierkant beeldblok",
          price: "€175 / maand",
          perks: [
            "1:1 afbeelding tussen de app-vermeldingen",
            "Roteert op categoriepagina's",
            "Klik- en impressie-statistieken",
          ],
        },
        {
          key: "featured",
          name: "Uitgelichte app",
          price: "€299 / maand",
          perks: [
            "Uitgelichte plek in de gids & rankings",
            "Gemarkeerde vermelding in je categorie",
            "Tot 3 screenshots + video demo",
          ],
        },
        {
          key: "banner",
          name: "Banneradvertentie",
          price: "€499 / maand",
          perks: [
            "Banner op de homepage",
            "Banner op categoriepagina's",
            "Klik- en impressie-statistieken",
          ],
        },
        {
          key: "video",
          name: "Videobanner",
          price: "€650 / maand",
          perks: [
            "15–30s autoplay-video op de homepage",
            "Premium-positie op categoriepagina's",
            "Gedetailleerde engagement-metrics",
          ],
        },
      ],
      sectors: [
        "Eigenaren",
        "Ruiters",
        "Trainers",
        "Fokkers",
        "Stallen & pensionstallen",
        "Dierenartsen",
        "Hoefsmeden",
        "Equine SaaS",
        "Voeding & supplementen",
      ],
      countries: ["Nederland", "België", "Duitsland", "Frankrijk", "VK", "VS", "Australië", "Spanje", "Zweden", "Italië"],
      idealFor: "Ideaal voor equine SaaS, fokkerijen, supplementenmerken, verzekeringen, opleidingen en paardensportretail.",
      form: {
        title: "Plan jouw campagne",
        company: "Bedrijf",
        name: "Jouw naam",
        email: "E-mail",
        website: "Website",
        packageLabel: "Interesse in",
        message: "Vertel ons over je doelgroep, land en doelen",
        submit: "Verstuur aanvraag",
        success: "Bedankt — ons advertentieteam neemt binnen 1 werkdag contact op.",
        error: "Er ging iets mis. Probeer het opnieuw.",
      },
    },
    footer: {
      tagline: "Het wereldwijde paardensoftware intelligence platform.",
      rights: "Alle rechten voorbehouden.",
    },
  },
};

// Region key normalisation (UI label -> backend region value)
export const REGION_KEYS = {
  Worldwide: "Worldwide",
  Wereldwijd: "Worldwide",
  Europe: "Europe",
  Europa: "Europe",
  USA: "USA",
  "Verenigde Staten": "USA",
  Australia: "Australia",
  Australië: "Australia",
};
